import { useState, type ReactElement, type ReactNode } from 'react';

import { AppIcons, RailCount, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

/* ────────────────────────── TrailingOverlay ────────────────────────── */

/**
 * The single absolute element in the rail: a hover/focus-revealed
 * action (settings icon, menu trigger) that overlays the faded
 * `RailCount` on its row. Anchored at `right-2` so its xs IconButton
 * box lands exactly on the count slot's column — count out, action in,
 * no reflow.
 */
function TrailingOverlay({ children }: Readonly<{ children: ReactNode }>): React.JSX.Element {
  return (
    <span className="absolute inset-y-0 right-2 inline-flex items-center opacity-0 transition-opacity duration-fast ease-standard group-hover/row:opacity-100 group-focus-within/row:opacity-100">
      {children}
    </span>
  );
}

/* ────────────────────────── Item (sub-menu) ────────────────────────── */

interface GroupedRailItemProps {
  title: string;
  isActive?: boolean;
  count?: number;
  trailing?: ReactNode;
  onClick?: () => void;
}

function Item({
  title,
  isActive = false,
  count,
  trailing,
  onClick,
}: Readonly<GroupedRailItemProps>): React.JSX.Element {
  const rowClassName = twcn(
    'flex h-row-h-compact w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2 text-left text-xs transition-colors duration-fast ease-standard',
    isActive
      ? 'bg-primary-tint font-medium text-primary-ink shadow-inset-primary-soft'
      : 'text-muted-foreground hover:bg-subtle hover:text-foreground',
  );

  const body = (
    <>
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {count == null ? null : <RailCount value={count} active={isActive} />}
    </>
  );

  return (
    <div className="group/row relative flex items-center">
      {onClick == null ? (
        <div className={rowClassName}>{body}</div>
      ) : (
        <button type="button" onClick={onClick} className={rowClassName}>
          {body}
        </button>
      )}
      {trailing == null ? null : <TrailingOverlay>{trailing}</TrailingOverlay>}
    </div>
  );
}

/* ────────────────────────── Button (action row) ────────────────────────── */

interface GroupedRailButtonProps {
  title: string;
  icon?: AppIconName;
  onClick: () => void;
}

function ActionButton({
  title,
  icon,
  onClick,
}: Readonly<GroupedRailButtonProps>): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-row-h-compact w-full items-center gap-1.5 rounded-sm border-0 bg-transparent px-2 text-left text-xs italic text-faint-foreground transition-colors duration-fast ease-standard hover:bg-subtle hover:text-foreground [&>svg]:flex-shrink-0 [&>svg]:text-faint-foreground hover:[&>svg]:text-foreground"
    >
      {icon == null ? null : <AppIcons name={icon} size={12} />}
      <span className="min-w-0 flex-1 truncate">{title}</span>
    </button>
  );
}

/* ────────────────────────── Group (menu) ────────────────────────── */

type GroupChild =
  | ReactElement<GroupedRailItemProps, typeof Item>
  | ReactElement<GroupedRailButtonProps, typeof ActionButton>;

interface GroupedRailGroupProps {
  title: string;
  isActive?: boolean;
  count?: number;
  trailing?: ReactNode;
  children: GroupChild | GroupChild[];
}

/**
 * GroupedRail.Group — collapsible menu row. The group starts collapsed
 * by default and toggles via the head; when `isActive` is true the
 * group is forced open and the caret reflects that state, but the row keeps
 * its default appearance. Active background lives on the
 * leaf-level `Item` instead. Pass `count` for a trailing counter
 * (mono, tabular) that the hover/focus state swaps with `trailing`.
 */
function Group({
  title,
  isActive = false,
  count,
  trailing,
  children,
}: Readonly<GroupedRailGroupProps>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const expanded = open || isActive;

  const headClassName =
    'flex h-row-h w-full flex-1 items-center gap-2 rounded-sm border-0 bg-transparent px-2 text-left text-meta font-medium text-foreground transition-colors duration-fast ease-standard hover:bg-subtle';

  return (
    <div className="flex flex-col">
      <div className="group/row relative flex items-center">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => {
            setOpen((o) => !o);
          }}
          className={headClassName}
        >
          <AppIcons
            name="caret-down"
            size={12}
            className={twcn(
              'flex-shrink-0 text-faint-foreground transition-transform duration-base ease-standard',
              !expanded && '-rotate-90',
            )}
          />
          <span className="min-w-0 flex-1 truncate">{title}</span>
          {count == null ? null : <RailCount value={count} active={false} />}
        </button>
        {trailing == null ? null : <TrailingOverlay>{trailing}</TrailingOverlay>}
      </div>
      {expanded ? (
        <div className="ml-1.5 flex flex-col gap-px border-l border-border-soft py-1 pl-3">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────── Root ────────────────────────── */

type GroupElement = ReactElement<GroupedRailGroupProps, typeof Group>;

interface GroupedRailProps {
  title: string;
  trailing?: ReactNode;
  children: GroupElement | GroupElement[];
}

/**
 * GroupedRail — collapsible-style section for the sidebar.
 *
 * Composes as:
 *   <GroupedRail title="..." trailing={<AddBtn/>}>
 *     <GroupedRail.Group title="..." isActive count={n} trailing={<EditBtn/>}>
 *       <GroupedRail.Item title="..." isActive count={n} trailing={<CogBtn/>} />
 *       <GroupedRail.Item title="..." />
 *       <GroupedRail.Button title="Adicionar squad" icon="plus" onClick={...} />
 *     </GroupedRail.Group>
 *   </GroupedRail>
 *
 * `GroupedRail` accepts only `Group` children; each `Group` accepts
 * `Item` and `Button` children. Groups collapse by default and force
 * open when `isActive` is set — without changing their background.
 * When both `count` and `trailing` are passed on a row, hover/focus
 * fades the counter and reveals the trailing element.
 */
function GroupedRailRoot({
  title,
  trailing,
  children,
}: Readonly<GroupedRailProps>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-px px-2">
      <div className="flex items-center justify-between px-2 pb-1 pt-1.5 font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
        <span>{title}</span>
        {trailing}
      </div>
      {children}
    </div>
  );
}

export const GroupedRail = Object.assign(GroupedRailRoot, {
  Group,
  Item,
  Button: ActionButton,
});
